import { Component, OnInit, input, output } from '@angular/core';
import { View } from '../../shared/classes/view.enum';
import { Order } from '../../shared/classes/order';
import { OrderStatus } from '../../shared/classes/order-status.enum';
import { FormsModule } from '@angular/forms';
import { NgxPaginationModule } from 'ngx-pagination';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css'],
    imports: [FormsModule, NgxPaginationModule]
})
/**
 * The home page which has options to create different types of orders.
 */
export class HomeComponent implements OnInit {
    public readonly orders = input.required<Order[]>();
    public readonly viewChange = output<View>();
    public readonly selectedOrderChange = output<Order>();

    public submittedOrders: Order[] = [];
    /** showResubmission: whether to show submitted orders. Used when selecting an order to resubmit */
    public showResubmission = false;
    /** page: current page of submitted orders when selecting an order to resubmit */
    public page = 1;
    public filterString = '';

    /**
     * Filters out the orders which can be selected for resubmission and
     * sorts them by date.
     */
    ngOnInit(): void {
        this.filterSubmittedOrders();
    }

    filterSubmittedOrders(): void {
        const orders = this.orders();
        this.submittedOrders = orders
            .filter((order) => {
                return (
                    order.status === OrderStatus.SUBMITTED &&
                    ((order.FlandersID && order.FlandersID.indexOf(this.filterString) > -1) ||
                        (order.SwordsID && order.SwordsID.indexOf(this.filterString) > -1))
                );
            })
            .sort((a: Order, b: Order) => {
                return a.submissionDate.getTime() - b.submissionDate.getTime();
            });
    }

    /**
     * Adds a new standard order to the user's orders array and changes to
     * that view.
     */
    makeStandardOrder(): void {
        const newOrder = new Order('New Standard Order', OrderStatus.INPROGRESS, new Date(), false, false);
        newOrder.index = undefined;
        newOrder.unsaved = true;
        newOrder.newOrder = true;
        this.orders().push(newOrder);
        this.selectedOrderChange.emit(newOrder);
        this.switchToOrderView();
    }

    /**
     * Adds a new just-documents order to the user's orders array and changes
     * to that view.
     */
    makeJustDocumentsOrder(): void {
        const newOrder = new Order('New Just-Documents Order', OrderStatus.INPROGRESS, new Date(), true, false);
        newOrder.index = undefined;
        newOrder.unsaved = true;
        newOrder.newOrder = true;
        this.orders().push(newOrder);
        this.selectedOrderChange.emit(newOrder);
        this.switchToOrderView();
    }

    /**
     * Adds a new resubmission order based on another order to the user's
     * orders array and changes to that view.
     *
     * @param order Order on which the new resubmission order should be based
     */
    makeResubmissionOrder(order: Order): void {
        const newOrder = order.clone();
        newOrder.index = undefined;
        newOrder.name = order.name + ' (Resubmission)';
        newOrder.status = OrderStatus.INPROGRESS;
        newOrder.startedDate = new Date();
        newOrder.comments = null;
        newOrder.submissionDate = undefined;
        newOrder.resubmission = true;
        newOrder.unsaved = true;
        newOrder.newOrder = true;
        this.orders().push(newOrder);
        this.showResubmission = !this.showResubmission;
        this.selectedOrderChange.emit(newOrder);
        this.switchToOrderView();
    }

    /**
     * Toggles whether the submitted orders are displayed for selection.
     */
    toggleSubmittedOrders(): void {
        this.showResubmission = !this.showResubmission;
    }

    /**
     * Changes the parent component's view to the Existing Order view.
     */
    switchToOrderView(): void {
        this.viewChange.emit(View.ORDER);
    }
}
